import React, { useCallback } from 'react';
import type { UnifiedCitation } from '../../types/citation';
import { toDocumentViewRequest } from '../../types/citation';
import { useDocumentViewer } from '../../contexts/DocumentViewerContext';

/**
 * Fixed palette of distinct colours for source-document dots.
 * Each entry is [text colour, hover colour, dark-mode text, dark-mode hover].
 */
const DOT_PALETTE = [
  ['text-cyan-600',   'hover:text-cyan-800',   'dark:text-cyan-400',   'dark:hover:text-cyan-300'],
  ['text-emerald-600','hover:text-emerald-800', 'dark:text-emerald-400','dark:hover:text-emerald-300'],
  ['text-amber-600',  'hover:text-amber-800',   'dark:text-amber-400',  'dark:hover:text-amber-300'],
  ['text-rose-600',   'hover:text-rose-800',    'dark:text-rose-400',   'dark:hover:text-rose-300'],
  ['text-violet-600', 'hover:text-violet-800',  'dark:text-violet-400', 'dark:hover:text-violet-300'],
  ['text-cyan-600',   'hover:text-cyan-800',    'dark:text-cyan-400',   'dark:hover:text-cyan-300'],
  ['text-orange-600', 'hover:text-orange-800',  'dark:text-orange-400', 'dark:hover:text-orange-300'],
  ['text-pink-600',   'hover:text-pink-800',    'dark:text-pink-400',   'dark:hover:text-pink-300'],
] as const;

/** Deterministic colour index from a document ID string. */
export function dotColourIndex(documentId: string): number {
  let hash = 0;
  for (let i = 0; i < documentId.length; i++) {
    hash = ((hash << 5) - hash + documentId.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % DOT_PALETTE.length;
}

/** Returns the Tailwind classes for a given colour index. */
export function dotColourClasses(index: number): string {
  const p = DOT_PALETTE[index % DOT_PALETTE.length];
  return `${p[0]} ${p[1]} ${p[2]} ${p[3]}`;
}

interface CitationSuperscriptProps {
  citation: UnifiedCitation;
  number: number;
}

/**
 * Tiny coloured dot that replaces numbered superscripts.
 * Same document → same colour. Click opens the cited document.
 */
export const CitationSuperscript: React.FC<CitationSuperscriptProps> = ({ citation, number }) => {
  const { openDocument } = useDocumentViewer();

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!citation.documentId) return;
    openDocument(toDocumentViewRequest(citation));
  }, [citation, openDocument]);

  const tooltip = `${citation.documentName || 'Document'}${citation.pageNumber != null ? `, p.${citation.pageNumber}` : ''}`;
  const colourIdx = dotColourIndex(citation.documentId);

  return (
    <sup
      onClick={handleClick}
      title={tooltip}
      className={`text-[10px] leading-none font-bold cursor-pointer select-none ml-px transition-colors ${dotColourClasses(colourIdx)}`}
    >
      •
    </sup>
  );
};
